import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, ArrowRight } from "lucide-react";
import { DashboardShell } from "@/components/dravian/dashboard-shell";
import { RiskPill } from "@/components/dravian/risk-pill";
import { WorkspaceCardMenu } from "@/components/dravian/workspace-card-menu";
import { publications as publicationsApi, type Publication, type PublicationMode } from "@/lib/api";
import { useWorkspaces, type Workspace } from "@/store/workspaces";

export const Route = createFileRoute("/workspaces/")({
  head: () => ({ meta: [{ title: "Workspaces — Dravian Horizon" }] }),
  component: WorkspacesPage,
});

function WorkspacesPage() {
  const { workspaces } = useWorkspaces();
  return (
    <DashboardShell>
      
      <main className="flex-1 mx-auto w-full max-w-[1400px] px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="label-eyebrow mb-1">Workspaces</div>
            <h1 className="text-[28px] font-semibold tracking-tight">All Workspaces</h1>
          </div>
          <Link to="/workspaces/new" className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-[13px] font-medium hover:bg-primary/90 glow-primary">
            <Plus className="h-4 w-4" /> New Workspace
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {workspaces.map((w) => (
            <Link key={w.id} to="/$workspace/geographic" params={{ workspace: w.id }} className="panel p-5 hover:border-primary/40 transition group">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <h3 className="text-[15px] font-semibold truncate">{w.name}</h3>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{w.domain.replace("_"," ")}</div>
                </div>
                <div className="flex items-center gap-1">
                  <RiskPill level={w.risk} />
                  <WorkspaceCardMenu workspace={w} />
                </div>
              </div>
              <ShareBadges workspace={w} />
              <PublicationBadges workspaceId={w.id} />
              <p className="text-[12px] text-muted-foreground mt-2 line-clamp-2">{w.description}</p>
              <div className="grid grid-cols-3 gap-2 mt-4">
                {[["Entities", w.entities],["Signals", w.signals],["Score", w.riskScore]].map(([l,v]) => (
                  <div key={String(l)} className="rounded border border-border bg-surface-elevated/40 px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{l}</div>
                    <div className="text-[14px] font-mono">{v}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4 text-[11px] text-muted-foreground">
                <span>{w.updatedAt}</span>
                <ArrowRight className="h-4 w-4 group-hover:text-primary group-hover:translate-x-0.5 transition" />
              </div>
            </Link>
          ))}
        </div>
      </main>

    </DashboardShell>
  );
}

const MODE_LABEL: Record<PublicationMode, string> = {
  private: "Private",
  shared_with_user: "Shared",
  org_shared: "Org shared",
  link_token: "Link snapshot",
  public: "Public snapshot",
};

const MODE_TONE: Record<PublicationMode, string> = {
  private: "border-neutral-500/30 text-neutral-400",
  shared_with_user: "border-blue-500/30 text-blue-400",
  org_shared: "border-blue-500/30 text-blue-400",
  link_token: "border-amber-500/30 text-amber-400",
  public: "border-rose-500/30 text-rose-400",
};

function PublicationBadges({ workspaceId }: { workspaceId: string }) {
  const q = useQuery<Publication[]>({
    queryKey: ["publications", workspaceId],
    queryFn: () => publicationsApi.list(workspaceId),
    retry: false,
    staleTime: 1000 * 60,
  });
  const active = (q.data ?? []).filter((p) => p.status === "active");
  if (active.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {active.map((p) => (
        <span
          key={p.id}
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-surface-elevated/40 ${MODE_TONE[p.mode]}`}
          title={p.hostname ?? ""}
        >
          {MODE_LABEL[p.mode]}
          {p.hostname && (
            <span className="ml-1.5 font-mono normal-case text-muted-foreground">
              · {p.hostname}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

function ShareBadges({ workspace }: { workspace: Workspace }) {
  // Decide which share-state badge to show. Public-snapshot is rendered
  // separately by PublicationBadges and can coexist with these.
  const badges: { label: string; tone: string }[] = [];
  if (workspace.share_origin === "explicit_member") {
    badges.push({
      label: "Shared with me",
      tone: "border-blue-500/30 text-blue-400",
    });
  } else if (workspace.org_shared) {
    badges.push({
      label: "Org shared",
      tone: "border-blue-500/30 text-blue-400",
    });
  }
  if (badges.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {badges.map((b) => (
        <span
          key={b.label}
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-surface-elevated/40 ${b.tone}`}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}